import { LazyLoader } from "@NextSkyTheme/lazy-load";

function safeParseStorage(key, fallback = []) {
  try {
    const value = localStorage.getItem(key);
    if (!value) return fallback;
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    console.warn(`Unable to read ${key} from localStorage.`, error);
    safeRemoveStorage(key);
    return fallback;
  }
}

function safeRemoveStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Unable to remove ${key} from localStorage.`, error);
  }
}

function safeSetStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Unable to write ${key} to localStorage.`, error);
  }
}

function safeParseJson(value, fallback = null) {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch (error) {
    console.warn("Unable to parse product JSON.", error);
    return fallback;
  }
}

function addProductEntry(productJson, storedProducts) {
  if (storedProducts === null) storedProducts = [];
  const currentProductID = productJson.toString();
  if (!storedProducts.includes(currentProductID)) {
    storedProducts.unshift(currentProductID);
    if (storedProducts.length > 25) {
      storedProducts.pop();
    }
    safeSetStorage("recently-viewed-products", storedProducts);
  } else {
    const index = storedProducts.indexOf(currentProductID);
    if (index > -1) {
      storedProducts.splice(index, 1);
    }
    storedProducts.unshift(currentProductID);
    safeSetStorage("recently-viewed-products", storedProducts);
  }
}

function recordRecentlyViewedProduct(scope = document) {
  const prodData = scope.querySelector("[data-product-json]");
  if (!prodData) return;

  const productJson = safeParseJson(prodData.textContent);
  if (productJson == null) return;

  const storedProducts = safeParseStorage("recently-viewed-products", []);
  addProductEntry(productJson, storedProducts);
}

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => recordRecentlyViewedProduct(),
    { once: true },
  );
} else {
  recordRecentlyViewedProduct();
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.abortController = null;
    this.hasLoaded = false;
    this.isLoading = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this.hasLoaded || this.isLoading || this.observer) return;
    if (typeof IntersectionObserver === "undefined") {
      this.loadRecommendations();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.disconnectObserver();
        this.loadRecommendations();
      },
      {
        rootMargin: "0px 0px 400px 0px",
      },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.disconnectObserver();
    this.abortFetch();
  }

  loadRecommendations() {
    if (!this.dataset.url) return;
    this.abortFetch();
    this.isLoading = true;
    this.abortController = new AbortController();
    const controller = this.abortController;
    fetch(this.dataset.url, { signal: controller.signal })
      .then((response) => response.text())
      .then((text) => {
        if (!this.isConnected || this.abortController !== controller) return;
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
        this.hasLoaded = true;
        new LazyLoader(".image-lazy-load");
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        console.error(e);
      })
      .finally(() => {
        if (this.abortController === controller) {
          this.abortController = null;
          this.isLoading = false;
        }
      });
  }

  disconnectObserver() {
    if (!this.observer) return;
    this.observer.unobserve(this);
    this.observer.disconnect();
    this.observer = null;
  }

  abortFetch() {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
    this.isLoading = false;
  }
}
if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

class StickyAddCart extends HTMLElement {
  constructor() {
    super();
    this._stickyScrollHandler = null;
    this._stickyFrame = null;
    this._isPrimaryButtonVisible = true;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this._stickyScrollHandler) return;
    this.main = document.querySelector(".main-product-section");
    if (!this.main) return;
    const primaryBtn = this.main.querySelector(".block-product__buttons");
    const footer = document.querySelector("footer");
    if (!primaryBtn) return;
    const updateStickyState = () => {
      this._stickyFrame = null;
      if (!this.isConnected || !primaryBtn.isConnected) return;

      const buttonRect = primaryBtn.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        if (footerRect.top < window.innerHeight - 100) {
          this.classList.remove("show-sticky-cart");
          document.documentElement.classList.remove("show-sticky-cart-active");
          this._isPrimaryButtonVisible = true;
        } else {
          if (
            !this._isPrimaryButtonVisible &&
            buttonRect.top < viewportHeight &&
            buttonRect.bottom > 0
          ) {
            this._isPrimaryButtonVisible = true;
            this.classList.remove("show-sticky-cart");
            document.documentElement.classList.remove(
              "show-sticky-cart-active"
            );
          } else if (this._isPrimaryButtonVisible && buttonRect.bottom <= 0) {
            this._isPrimaryButtonVisible = false;
            this.classList.add("show-sticky-cart");
            document.documentElement.classList.add("show-sticky-cart-active");
          }
        }
      } else {
        if (
          !this._isPrimaryButtonVisible &&
          buttonRect.top < viewportHeight &&
          buttonRect.bottom > 0
        ) {
          this._isPrimaryButtonVisible = true;
          this.classList.remove("show-sticky-cart");
          document.documentElement.classList.remove("show-sticky-cart-active");
        } else if (this._isPrimaryButtonVisible && buttonRect.bottom <= 0) {
          this._isPrimaryButtonVisible = false;
          this.classList.add("show-sticky-cart");
          document.documentElement.classList.add("show-sticky-cart-active");
        }
      }
    };
    this._stickyScrollHandler = () => {
      if (this._stickyFrame) return;
      this._stickyFrame = requestAnimationFrame(updateStickyState);
    };
    window.addEventListener("scroll", this._stickyScrollHandler, {
      passive: true,
    });
    this._stickyScrollHandler();
  }

  disconnectedCallback() {
    if (this._stickyScrollHandler) {
      window.removeEventListener("scroll", this._stickyScrollHandler);
      this._stickyScrollHandler = null;
    }
    if (this._stickyFrame) {
      cancelAnimationFrame(this._stickyFrame);
      this._stickyFrame = null;
    }
    this._isPrimaryButtonVisible = true;
    this.classList.remove("show-sticky-cart");
    document.documentElement.classList.remove("show-sticky-cart-active");
  }
}
if (!customElements.get("sticky-add-cart")) {
  customElements.define("sticky-add-cart", StickyAddCart);
}

const infoGalleryHandlers = new WeakMap();

const infoGallerySlide = (scope = document) => {
  const gallery = scope.querySelectorAll(".info-gallery__card");
  gallery.forEach((e) => {
    if (infoGalleryHandlers.has(e)) return;
    const mouseoverHandler = (event) => {
      const currentTarget = event.currentTarget;
      if (!currentTarget.classList.contains("active")) {
        gallery.forEach((el) => {
          el.classList.remove("active");
        });
        currentTarget.classList.add("active");
      }
    };
    e.addEventListener("mouseover", mouseoverHandler);
    infoGalleryHandlers.set(e, mouseoverHandler);
  });
};

const cleanupInfoGallerySlide = (scope = document) => {
  scope.querySelectorAll(".info-gallery__card").forEach((card) => {
    const mouseoverHandler = infoGalleryHandlers.get(card);
    if (!mouseoverHandler) return;
    card.removeEventListener("mouseover", mouseoverHandler);
    infoGalleryHandlers.delete(card);
  });
};

document.addEventListener("shopify:section:load", function (event) {
  recordRecentlyViewedProduct(event.target || document);
  infoGallerySlide(event.target || document);
});
document.addEventListener("shopify:section:unload", function (event) {
  cleanupInfoGallerySlide(event.target || document);
});
infoGallerySlide();
